import { useEffect, useMemo, useState } from "react";
import { fmtCAD } from "../../lib/cataley";

const COLORS = ["#f59e0b", "#10b981", "#6366f1", "#ec4899", "#38bdf8", "#facc15"];

// 🎉 Coffre d'objectif atteint — fête pleine page + étoile bonus quand un rêve passe à 100 %.
export default function GoalCelebration({ goal, onOpen, onClose }) {
  const [opened, setOpened] = useState(false);
  const [result, setResult] = useState(null);
  const pieces = useMemo(() => Array.from({ length: 70 }, (_, i) => ({
    id: i, left: Math.random() * 100, delay: Math.random() * 1.5, dur: 2.5 + Math.random() * 2,
    color: COLORS[i % COLORS.length], size: 6 + Math.random() * 8, rot: Math.random() * 360,
  })), []);

  useEffect(() => {
    if (!opened) return;
    let alive = true;
    onOpen().then((r) => alive && setResult(r || { celebrated: false }));
    return () => { alive = false; };
  }, [opened, onOpen]);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-indigo-950/80 p-6 backdrop-blur-sm" data-testid="cataley-celebration">
      <style>{`
        @keyframes cataley-fall { 0% { transform: translateY(-10vh) rotate(0deg); opacity: 1 } 100% { transform: translateY(110vh) rotate(720deg); opacity: .9 } }
        @keyframes cataley-pop { 0% { transform: scale(.6) rotate(-6deg); opacity: 0 } 60% { transform: scale(1.08) rotate(2deg); opacity: 1 } 100% { transform: scale(1) rotate(0) } }
        @keyframes cataley-shake { 0%,100% { transform: rotate(0) } 20% { transform: rotate(-8deg) } 40% { transform: rotate(8deg) } 60% { transform: rotate(-6deg) } 80% { transform: rotate(6deg) } }
        @keyframes cataley-star { 0% { transform: translateY(20px) scale(.3); opacity: 0 } 50% { transform: translateY(-30px) scale(1.4); opacity: 1 } 100% { transform: translateY(-20px) scale(1); opacity: 1 } }
      `}</style>
      {opened && pieces.map((p) => (
        <span key={p.id} className="pointer-events-none absolute top-0 block rounded-sm"
          style={{ left: `${p.left}%`, width: p.size, height: p.size * 0.6, background: p.color, transform: `rotate(${p.rot}deg)`,
            animation: `cataley-fall ${p.dur}s linear ${p.delay}s infinite` }} />
      ))}
      <div className="relative w-full max-w-sm rounded-3xl bg-white p-6 text-center shadow-2xl" style={{ animation: "cataley-pop .5s ease-out" }}>
        {!opened ? (
          <>
            <div className="text-sm font-semibold uppercase tracking-wider text-indigo-500">Coffre d'objectif atteint</div>
            {goal.image_url && <img src={goal.image_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${goal.image_url}` : goal.image_url} alt="" className="mx-auto mt-3 h-28 w-28 rounded-2xl object-cover shadow" />}
            <h2 className="mt-2 text-2xl font-black text-stone-800">Ton rêve est à 100 % !</h2>
            <p className="mt-1 text-stone-600">« {goal.title} » — {fmtCAD(goal.target_cents)} mis de côté. Bravo !</p>
            <button onClick={() => setOpened(true)} data-testid="cataley-open-chest"
              className="mx-auto mt-5 grid h-28 w-28 place-items-center rounded-3xl bg-gradient-to-br from-amber-300 to-amber-500 shadow-lg transition-transform hover:scale-105 active:scale-95"
              aria-label="Ouvrir le coffre"><span className="block text-6xl" style={{ animation: "cataley-shake 1.2s ease-in-out infinite" }}>🧰</span></button>
            <p className="mt-3 text-sm font-semibold text-amber-600">Touche le coffre pour l'ouvrir !</p>
          </>
        ) : (
          <>
            {goal.image_url && <img src={goal.image_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${goal.image_url}` : goal.image_url} alt="" data-testid="cataley-celebration-image"
              className="mx-auto mb-3 h-44 w-full rounded-2xl object-cover shadow-lg" style={{ animation: "cataley-pop .7s ease-out" }} />}
            <div className="text-6xl" style={{ animation: "cataley-star 1s ease-out forwards" }}>⭐</div>
            <h2 className="mt-2 text-2xl font-black text-stone-800">Hourra {"\u{1F389}"}</h2>
            <p className="mt-1 text-stone-600">Tu as réussi à épargner pour « {goal.title} ».</p>
            <p className="mt-3 rounded-2xl bg-amber-50 px-4 py-2 text-sm font-bold text-amber-700" data-testid="cataley-bonus-star">
              {result?.celebrated ? "+1 étoile bonus pour ta patience !" : result ? "Tu avais déjà reçu ton étoile pour ce rêve ✨" : "…"}
            </p>
            <p className="mt-2 text-xs text-stone-500">Maintenant, va chercher ton rêve avec papa !</p>
            <button onClick={onClose} data-testid="cataley-celebration-close"
              className="mt-5 rounded-full bg-emerald-500 px-6 py-2 font-bold text-white shadow hover:bg-emerald-600">Youpi !</button>
          </>
        )}
      </div>
    </div>
  );
}
